import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	RespyCalibration,
	SiteWithCalibration,
} from "./respyCalibrationManagement.model";
import { Autocomplete, TextField, Button, Box } from "@mui/material";
import { apiGet, apiPatch } from "../../common/utils/request.utils";
import { If } from "../../common/components/If";

export interface ActiveCalibrationFieldProps {
	value: RespyCalibration;
	site: SiteWithCalibration;
}

export function ActiveCalibrationField(
	props: Readonly<ActiveCalibrationFieldProps>
) {
	const [value, setValue] = useState<RespyCalibration | null>(null);
	const [dirty, setDirty] = useState(false);
	const [searchLoading, setSearchLoading] = React.useState(false);
	const [updateLoading, setUpdateLoading] = React.useState(false);
	const [inputValue, setInputValue] = React.useState("");
	const [options, setOptions] = React.useState<RespyCalibration[]>([]);

	const isLoading = useMemo(
		() => searchLoading || updateLoading,
		[searchLoading, updateLoading]
	);

	useEffect(() => {
		setValue(props.value);
	}, [props.value]);

	useEffect(() => {
		const controller = new AbortController();
		setSearchLoading(true);
		apiGet("/respy-calibrations/" + props.site.asset_id, {
			signal: controller.signal,
			params: { search: inputValue },
		})
			.then((response) => {
				setOptions(response.data);
				setSearchLoading(false);
			})
			.catch((error) => {
				if (!error.__CANCEL__) {
					setSearchLoading(false);
				}
			});
		return () => controller.abort();
	}, [inputValue, props.site]);

	const save = useCallback(async () => {
		setUpdateLoading(true);
		try {
			await apiPatch("/respy-calibrations/" + props.site.asset_id, value);
			setDirty(false);
		} finally {
			setUpdateLoading(false);
		}
	}, [value, setDirty, setUpdateLoading, props.site]);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				margin: "0 16px",
				gap: "16px",
				minWidth: "380px",
			}}
		>
			<Autocomplete
				disabled={updateLoading}
				sx={{ display: "flex", flex: 1 }}
				isOptionEqualToValue={(option, value) =>
					option.calibration_name === value.calibration_name
				}
				getOptionLabel={(option) => option.calibration_name}
				options={options}
				loading={isLoading}
				value={value}
				onInputChange={(event, newInputValue) => {
					setInputValue(newInputValue);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Active calibration"
						fullWidth
					/>
				)}
				onChange={(ev, newValue) => {
					setValue(newValue);
					setDirty(true);
				}}
			/>
			<Box sx={{ width: "24px" }}>
				<If condition={dirty}>
					<Button
						variant="contained"
						disabled={isLoading}
						onClick={save}
					>
						Save
					</Button>
				</If>
			</Box>
		</Box>
	);
}
